/**
 * Image utilities for chat image attachments.
 * Handles compression, conversion to base64, and validation.
 */

import type { ImageAttachment } from '@/app/app/research-assistant/types';

// Maximum image size in bytes (500KB - keeps Firestore docs under 1MB)
const MAX_IMAGE_SIZE = 500_000;

// Maximum image dimension (width or height)
const MAX_DIMENSION = 1920;

// Supported MIME types
const SUPPORTED_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/avif',
] as const;
type SupportedMimeType = (typeof SUPPORTED_MIME_TYPES)[number];
type CompressedMimeType = ImageAttachment['mimeType'];

/**
 * Check if a MIME type is supported
 */
export function isSupportedImageType(mimeType: string): mimeType is SupportedMimeType {
  return SUPPORTED_MIME_TYPES.includes(mimeType as SupportedMimeType);
}

/**
 * Load an image from a File and return dimensions
 */
function loadImage(file: File): Promise<{
  img: HTMLImageElement;
  width: number;
  height: number;
  revokeObjectUrl: () => void;
}> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    let revoked = false;
    const revokeObjectUrl = () => {
      if (revoked) return;
      URL.revokeObjectURL(objectUrl);
      revoked = true;
    };
    img.onload = () => {
      resolve({ img, width: img.naturalWidth, height: img.naturalHeight, revokeObjectUrl });
    };
    img.onerror = () => {
      revokeObjectUrl();
      reject(new Error('Failed to load image'));
    };
    img.src = objectUrl;
  });
}

/**
 * Compress and convert an image file to base64.
 * Resizes if too large and compresses to JPEG for efficiency.
 */
export async function compressImage(file: File): Promise<{
  base64: string;
  mimeType: CompressedMimeType;
  width: number;
  height: number;
}> {
  // Validate MIME type
  if (!isSupportedImageType(file.type)) {
    throw new Error(`Unsupported image type: ${file.type}. Supported: PNG, JPEG, WebP, GIF, AVIF`);
  }

  const { img, width, height, revokeObjectUrl } = await loadImage(file);

  try {
    // Calculate resize dimensions if needed
    let targetWidth = width;
    let targetHeight = height;

    if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
      const scale = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
      targetWidth = Math.round(width * scale);
      targetHeight = Math.round(height * scale);
    }

    // Create canvas for compression
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas context');

    // Draw image
    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

    // Try to compress as JPEG first (better compression)
    // For PNGs with transparency, try PNG then WebP
    let base64: string;
    let mimeType: CompressedMimeType;

    // Check if image might have transparency (PNG, WebP, GIF)
    const mightHaveTransparency = ['image/png', 'image/webp', 'image/gif', 'image/avif'].includes(
      file.type
    );

    if (mightHaveTransparency) {
      // Try WebP first (good compression, supports transparency)
      base64 = canvas.toDataURL('image/webp', 0.85).split(',')[1];
      mimeType = 'image/webp';

      // If WebP is too large, try PNG
      if (base64.length * 0.75 > MAX_IMAGE_SIZE) {
        base64 = canvas.toDataURL('image/png').split(',')[1];
        mimeType = 'image/png';
      }
    } else {
      // For JPEG, use JPEG compression
      base64 = canvas.toDataURL('image/jpeg', 0.85).split(',')[1];
      mimeType = 'image/jpeg';
    }

    // If still too large, reduce quality progressively
    if (base64.length * 0.75 > MAX_IMAGE_SIZE) {
      let quality = 0.7;
      while (base64.length * 0.75 > MAX_IMAGE_SIZE && quality > 0.3) {
        base64 = canvas.toDataURL('image/jpeg', quality).split(',')[1];
        mimeType = 'image/jpeg';
        quality -= 0.1;
      }
    }

    // Final check - if still too large, resize more aggressively
    if (base64.length * 0.75 > MAX_IMAGE_SIZE) {
      const scaleFactor = Math.sqrt(MAX_IMAGE_SIZE / (base64.length * 0.75));
      const newWidth = Math.round(targetWidth * scaleFactor);
      const newHeight = Math.round(targetHeight * scaleFactor);

      canvas.width = newWidth;
      canvas.height = newHeight;
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
      base64 = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
      mimeType = 'image/jpeg';
      targetWidth = newWidth;
      targetHeight = newHeight;
    }

    return {
      base64,
      mimeType,
      width: targetWidth,
      height: targetHeight,
    };
  } finally {
    revokeObjectUrl();
  }
}

/**
 * Process a File into an ImageAttachment
 */
export async function fileToImageAttachment(file: File): Promise<ImageAttachment> {
  const { base64, mimeType, width, height } = await compressImage(file);

  return {
    id: `img-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    mimeType,
    base64Data: base64,
    name: file.name,
    width,
    height,
  };
}

/**
 * Get image data URL from base64 for display
 */
export function getImageDataUrl(image: ImageAttachment): string {
  return `data:${image.mimeType};base64,${image.base64Data}`;
}

/**
 * Estimate token count for an image (for context budgeting)
 * Gemini charges ~258 tokens for images <= 384x384, scaling up for larger
 */
export function estimateImageTokens(image: ImageAttachment): number {
  const { width = 512, height = 512 } = image;

  // Gemini's image token calculation (approximate)
  // Small images (<=384x384): ~258 tokens
  // Larger images: scales based on tiles
  if (width <= 384 && height <= 384) {
    return 258;
  }

  // For larger images, estimate based on 768x768 tiles
  const tilesX = Math.ceil(width / 768);
  const tilesY = Math.ceil(height / 768);
  return tilesX * tilesY * 258;
}
